// Canned replies ("commands") as macros: placeholder expansion for the reply
// text, and the inbox changes a macro applies when its reply is sent. Pure,
// so the rules are unit tested (macro.test.ts).

export type MacroStatus = 'open' | 'pending' | 'resolved' | 'closed';
export type MacroPriority = 'low' | 'normal' | 'high' | 'urgent';

/** Inbox changes a command applies when the agent sends its reply. */
export interface MacroActions {
  status?: MacroStatus;
  priority?: MacroPriority;
  addTags?: string[];
}

export interface MacroContext {
  /** The visitor's real name; null/undefined for an anonymous visitor. */
  visitorName?: string | null;
  visitorEmail?: string | null;
  agentName?: string | null;
  siteName?: string | null;
}

/** Placeholders a reply can use, for the help text in the commands manager. */
export const PLACEHOLDERS = [
  '{{visitor.name}}',
  '{{visitor.first_name}}',
  '{{visitor.email}}',
  '{{agent.name}}',
  '{{agent.first_name}}',
  '{{site.name}}',
] as const;

/** Said instead of a name the visitor never gave ("Hi there,"). */
const NAMELESS_VISITOR = 'there';

function firstName(name: string | null | undefined): string {
  return name?.trim().split(/\s+/)[0] ?? '';
}

function resolve(key: string, ctx: MacroContext): { value: string; fallback: string } | null {
  switch (key) {
    case 'visitor':
    case 'visitor.name':
      return { value: ctx.visitorName?.trim() ?? '', fallback: NAMELESS_VISITOR };
    case 'visitor.first_name':
      return { value: firstName(ctx.visitorName), fallback: NAMELESS_VISITOR };
    case 'visitor.email':
      return { value: ctx.visitorEmail?.trim() ?? '', fallback: '' };
    case 'agent':
    case 'agent.name':
      return { value: ctx.agentName?.trim() ?? '', fallback: '' };
    case 'agent.first_name':
      return { value: firstName(ctx.agentName), fallback: '' };
    case 'site':
    case 'site.name':
      return { value: ctx.siteName?.trim() ?? '', fallback: '' };
    default:
      return null;
  }
}

/**
 * Fill `{{key}}` / `{{key|fallback}}` placeholders. A missing value uses the
 * fallback after `|`, else the key's default ("there" for a visitor's name).
 * Unknown keys stay as written, so a typo is visible in the composer before
 * the agent sends rather than silently vanishing.
 */
export function expandPlaceholders(body: string, ctx: MacroContext): string {
  return body.replace(/\{\{\s*([a-z_.]+)\s*(?:\|([^}]*))?\}\}/gi, (match, rawKey: string, explicit?: string) => {
    const resolved = resolve(rawKey.toLowerCase(), ctx);
    if (!resolved) return match;
    if (resolved.value) return resolved.value;
    return explicit !== undefined ? explicit.trim() : resolved.fallback;
  });
}

/** Most tags a conversation can carry (matches the server's limit). */
const MAX_TAGS = 10;

/** The conversation's tags after a macro adds its own: normalised like every
 *  stored tag, no duplicates, capped at MAX_TAGS. */
export function mergeTags(current: readonly string[], add: readonly string[] | undefined): string[] {
  const next = [...current];
  for (const raw of add ?? []) {
    const tag = raw.trim().toLowerCase();
    if (tag && !next.includes(tag)) next.push(tag);
  }
  return next.slice(0, MAX_TAGS);
}

/** True when the macro changes anything besides the reply text. */
export function hasActions(actions: MacroActions | null | undefined): boolean {
  return !!actions && (!!actions.status || !!actions.priority || (actions.addTags?.length ?? 0) > 0);
}

/** Short human summary for lists: "→ Resolved · Urgent · +#vip". */
export function describeActions(actions: MacroActions | null | undefined): string {
  if (!hasActions(actions)) return '';
  const parts: string[] = [];
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  if (actions!.status) parts.push(`→ ${cap(actions!.status)}`);
  if (actions!.priority) parts.push(cap(actions!.priority));
  for (const tag of actions!.addTags ?? []) parts.push(`+#${tag}`);
  return parts.join(' · ');
}
